import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import FAQ from 'components/ui/FAQ';
import RelatedBlogs from 'components/RelatedBlogs';
import { zapierIntegrationFAQs } from '../data/faqs';
import { useNavigate } from 'react-router-dom';

const ZapierIntegration = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'Zapier Integration Services - Workflow Automation | OctalChip',
    description: 'Professional Zapier integration services. We build and optimize Zapier workflows that connect your favorite SaaS tools and automate repetitive tasks without complexity.',
    keywords: 'zapier integration, zapier automation, zapier workflow, zapier development, zapier consulting, workflow automation, business automation, zapier services, automation services',
    canonical: 'https://www.octalchip.com/services/zapier-integration',
    faqData: zapierIntegrationFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Zapier Integration Services",
      "description": "Zapier workflow automation services to connect SaaS tools and automate business processes",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const zapierServices = [
    {
      title: 'Zap creation and optimization',
      description: 'Design and build efficient Zaps that connect your tools with optimal performance and reliability',
      icon: 'Zap',
      number: '01'
    },
    {
      title: 'Multi-step workflows',
      description: 'Create complex multi-step Zaps with conditional logic, filters, and data transformations',
      icon: 'Workflow',
      number: '02'
    },
    {
      title: 'Webhooks by Zapier',
      description: 'Set up webhook triggers and actions for custom integrations and real-time automation',
      icon: 'Link',
      number: '03'
    },
    {
      title: 'Error handling & retries',
      description: 'Implement robust error handling, retry logic, and monitoring for reliable automation',
      icon: 'AlertCircle',
      number: '04'
    },
    {
      title: 'Scaling existing automations',
      description: 'Optimize and scale your existing Zaps to handle increased volume and complexity',
      icon: 'TrendingUp',
      number: '05'
    }
  ];

  const useCases = [
    {
      title: 'Lead capture → CRM → Email follow-up',
      description: 'Automatically capture leads from forms, add to CRM, and trigger personalized email sequences',
      icon: 'Users',
      gradient: 'from-[#00d690] to-[#00b876]'
    },
    {
      title: 'E-commerce order processing',
      description: 'Process orders from your store, update inventory, send confirmations, and notify teams',
      icon: 'ShoppingCart',
      gradient: 'from-[#00b876] to-[#00d690]'
    },
    {
      title: 'Scheduling & calendar automation',
      description: 'Sync calendars, send reminders, schedule meetings, and manage bookings automatically',
      icon: 'Calendar',
      gradient: 'from-[#00d690] to-[#00b876]'
    },
    {
      title: 'Marketing campaign triggers',
      description: 'Automate marketing workflows based on user actions, events, and campaign performance',
      icon: 'Megaphone',
      gradient: 'from-[#00b876] to-[#00d690]'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section - Centered with Lightning Effect */}
      <section id="hero" className="relative py-12 md:py-20 lg:py-28 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00d690]/5 via-white to-[#00b876]/5">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#00d690]/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#00b876]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        </div>
        
        {/* Lightning Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-1/4 w-px h-32 bg-[#00d690] transform rotate-45"></div>
          <div className="absolute top-1/3 right-1/4 w-px h-24 bg-[#00b876] transform -rotate-45"></div>
          <div className="absolute bottom-1/4 left-1/3 w-px h-40 bg-[#00d690] transform rotate-12"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-full shadow-2xl mb-8 transform hover:scale-110 transition-transform duration-300">
              <Icon name="Zap" size={56} className="text-white" />
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
              <span className="text-gray-800">Zapier Automation for</span>
              <br />
              <span className="bg-gradient-to-r from-[#00d690] to-[#00b876] bg-clip-text text-transparent">
                Faster Business Operations
              </span>
            </h1>
            
            <div className="inline-block w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-8"></div>
            
            <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              We build and optimize Zapier workflows that connect your favorite SaaS tools and automate 
              repetitive tasks without complexity.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                variant="primary" 
                onClick={() => navigate('/contact#contact-form')}
                className="px-8 py-4 text-base shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
              >
                Automate with Zapier
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/services/workflow-automation#what-we-offer')}
                className="px-8 py-4 text-base border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
              >
                View All Automation Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Zapier Services Section - Numbered Cards */}
      <section id="zapier-services" className="py-16 md:py-24 relative">
        <div className="absolute inset-0 bg-gray-50 -skew-y-1"></div>
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Zapier Services
            </h2>
            <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive Zapier services to automate your business processes
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto space-y-6">
            {zapierServices.map((service, index) => (
              <div 
                key={index} 
                className="group relative bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[#00d690]/30"
              >
                <div className="flex items-start gap-6">
                  {/* Number Badge */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <span className="text-2xl font-bold text-white">{service.number}</span>
                    </div>
                  </div>
                  
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-[#00d690]/10 rounded-xl flex items-center justify-center group-hover:bg-[#00d690] transition-all duration-300">
                      <Icon name={service.icon} size={32} className="text-[#00d690] group-hover:text-white transition-colors" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section - Gradient Cards */}
      <section id="use-cases" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-white"></div>
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-50 to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Zapier Use Cases
            </h2>
            <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Common automation scenarios we build with Zapier
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${useCase.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                
                {/* Content */}
                <div className="relative bg-white p-6 md:p-8 group-hover:bg-transparent transition-colors duration-300">
                  <div className="w-14 h-14 bg-[#00d690]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-white/20 transition-all duration-300">
                    <Icon name={useCase.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-white transition-colors">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-white/90 transition-colors leading-relaxed">
                    {useCase.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white"></div>
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-[#00d690]/20 relative overflow-hidden">
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#00d690]/5 rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#00b876]/5 rounded-full blur-2xl"></div>
              
              <div className="relative z-10">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                  Ready to Automate with Zapier?
                </h2>
                <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
                <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                  Let's connect your tools and automate your workflows. Get started with Zapier automation today.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/contact#contact-form')}
                    className="bg-gradient-to-r from-[#00d690] to-[#00b876] hover:from-[#00b876] hover:to-[#00d690] text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    Automate with Zapier
                  </button>
                  <button
                    onClick={() => navigate('/services/workflow-automation#what-we-offer')}
                    className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-4 px-8 rounded-xl transition-all duration-300"
                  >
                    View All Automation Services
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={zapierIntegrationFAQs}
        title="Zapier Integration FAQs"
        description="Common questions about Zapier automation, workflows, and integration services."
      />

      {/* Related Blogs Section */}
      <RelatedBlogs
        servicePath="/services/zapier-integration"
        serviceTags={['Zapier', 'Zapier Integration', 'Workflow Automation', 'Automation', 'Integration']}
        serviceName="Zapier Integration"
        limit={6}
        title="Zapier Integration Related Blogs"
      />

      <Footer />
    </div>
  );
};

export default ZapierIntegration;
